import React, { useState } from 'react';
import MiniChart from './MiniChart';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const MetricCard = ({ metric }) => {
  const { name, label, value, icon, chartColor, metricType, description } = metric;
  const [expanded, setExpanded] = useState(false);
  
  // Determine the metric type based on the name
  const getMetricType = () => {
    if (metricType) return metricType;
    
    const lowerName = name.toLowerCase();
    if (lowerName.includes('soil')) return 'soil';
    if (lowerName.includes('water')) return 'water';
    if (lowerName.includes('nutrient')) return 'nutrient';
    if (lowerName.includes('volume')) return 'water';
    if (lowerName.includes('temperature')) return 'temperature';
    if (lowerName.includes('humidity')) return 'humidity';
    if (lowerName.includes('yield')) return 'yield';
    if (lowerName.includes('precipitation')) return 'precipitation';
    if (lowerName.includes('biodiversity')) return 'biodiversity';
    if (lowerName.includes('rotation')) return 'cropRotation';
    if (lowerName.includes('pest')) return 'pestResistance';
    if (lowerName.includes('carbon')) return 'carbonSequestration';
    return 'default';
  };

  // Determine status color based on value
  const getStatusColor = () => {
    if (!value) return 'var(--text-secondary)';
    
    // For percentage values
    if (value.includes('%')) {
      const numValue = parseFloat(value);
      if (numValue >= 80) return 'var(--accent-green)';
      if (numValue >= 60) return 'var(--accent-yellow)';
      return 'var(--accent-red)';
    }
    
    // For temperature values
    if (value.includes('°')) {
      const numValue = parseFloat(value);
      if (numValue >= 80 || numValue <= 32) return 'var(--accent-red)';
      if (numValue >= 70 || numValue <= 45) return 'var(--accent-yellow)';
      return 'var(--accent-green)';
    }
    
    return chartColor;
  };

  // Get recommendations based on metric type
  const getRecommendation = () => {
    switch(getMetricType()) {
      case 'soil':
        return 'Consider adding organic matter to improve soil structure.';
      case 'water':
        return parseFloat(value) < 70 ? 'Increase irrigation frequency.' : 'Water levels are optimal.';
      case 'nutrient':
        return parseFloat(value) < 50 ? 'Consider applying organic fertilizer.' : 'Nutrient levels are good.';
      case 'biodiversity':
        return 'Introduce more native plant species to increase biodiversity.';
      case 'soilHealth':
        return 'Implement cover crops to improve soil health.';
      case 'cropRotation':
        return 'Maintain current rotation schedule for optimal soil health.';
      case 'pestResistance':
        return 'Continue companion planting to maintain pest resistance.';
      case 'carbonSequestration':
        return 'Increase no-till practices to improve carbon sequestration.';
      case 'waterEfficiency':
        return 'Consider drip irrigation to improve water efficiency.';
      default:
        return 'Monitor and maintain current levels.';
    }
  };
  
  return (
    <div className={`card ${expanded ? 'expanded' : ''}`}>
      <div className="metric" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="metric-label">
            {name}
            <FaInfoCircle className="info-icon" title={description} />
          </div>
          {label && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{label}</div>}
        </div>
        <div className="metric-value" style={{ color: getStatusColor() }}>
          {value}
          {icon && <span style={{ marginLeft: '8px', fontSize: '16px' }}>{icon}</span>}
          <span className="expand-icon">
            {expanded ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
      </div>
      <div className="chart-container">
        <MiniChart 
          color={chartColor} 
          metricType={getMetricType()} 
          useDMVData={true}
          height={expanded ? 60 : 40}
          showTooltip={true}
        />
      </div>
      
      {expanded && (
        <div className="metric-details">
          <div className="metric-description">
            {description || `${name} is an important agricultural metric.`}
          </div>
          <div className="metric-recommendation">
            <strong>Recommendation:</strong> {getRecommendation()}
          </div>
          <div className="metric-trend">
            <strong>Trend:</strong> {
              getMetricType() === 'cropRotation' || getMetricType() === 'biodiversity' || 
              getMetricType() === 'soilHealth' || getMetricType() === 'waterEfficiency' ? 
              'Improving' : 'Stable'
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricCard; 